import { useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowLeft, Compass } from 'lucide-react';
import Footer from '../components/Footer';
import { getIntentPageBySlug } from '../lib/intentPages';
import { setPageSeo } from '../utils/seo';
import NotFoundPage from './NotFoundPage';

export default function IntentPage({ slug: slugProp }: { slug?: string }) {
  const params = useParams<{ slug: string }>();
  const slug = slugProp ?? params.slug;
  const page = slug ? getIntentPageBySlug(slug) : undefined;

  useEffect(() => {
    if (page) {
      setPageSeo({
        title: page.metaTitle,
        description: page.description,
        path: `/${page.slug}/`,
        type: 'article',
        jsonLd: {
          '@context': 'https://schema.org',
          '@type': 'WebPage',
          name: page.title,
          url: `https://trufflehuntingoregon.com/${page.slug}/`,
          description: page.description,
          isPartOf: {
            '@id': 'https://trufflehuntingoregon.com/#website',
          },
        },
      });
    }
  }, [page]);

  if (!page) {
    return <NotFoundPage />;
  }

  return (
    <div className="min-h-screen bg-bg text-ink">
      <header className="border-b border-border bg-white/85 backdrop-blur-sm sticky top-0 z-40">
        <div className="max-w-screen-2xl mx-auto px-6 md:px-12 py-4 flex items-center justify-between gap-4">
          <Link to="/" className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-accent text-white flex items-center justify-center">
              <Compass size={18} />
            </div>
            <div className="leading-tight">
              <div className="font-serif text-lg">Truffle Hunting Oregon</div>
              <div className="text-[10px] uppercase tracking-widest text-ink-light">Oregon guide</div>
            </div>
          </Link>
          <Link to="/" className="btn-minimal inline-flex items-center gap-2">
            <ArrowLeft size={14} /> Back to hub
          </Link>
        </div>
      </header>

      <main className="max-w-screen-2xl mx-auto px-6 md:px-12 py-16 md:py-20 space-y-12">
        <section className="grid grid-cols-1 xl:grid-cols-[1.05fr_0.95fr] gap-8 items-start">
          <div className="space-y-6">
            <span className="pill-minimal active">{page.eyebrow}</span>
            <h1 className="text-5xl md:text-7xl font-serif tracking-tight leading-[1.02]">{page.title}</h1>
            <p className="text-lg md:text-xl text-ink-light leading-relaxed max-w-4xl">{page.hero}</p>
          </div>

          <article className="card-minimal p-8 bg-sidebar/70 space-y-5">
            <span className="pill-minimal">Planning note</span>
            <h2 className="text-4xl font-serif tracking-tight">Useful, not reckless.</h2>
            <p className="text-lg text-ink-light leading-relaxed">
              {page.primaryCta}
            </p>
          </article>
        </section>

        <section className="grid grid-cols-1 xl:grid-cols-[0.85fr_1.15fr] gap-8 items-start">
          <aside className="card-minimal p-7 bg-white/80 space-y-4 xl:sticky xl:top-32">
            <span className="pill-minimal active">In this guide</span>
            <nav className="grid gap-3">
              {page.sections.map((section) => (
                <a key={section.title} href={`#${section.title.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`} className="text-sm text-ink-light hover:text-accent">
                  {section.title}
                </a>
              ))}
            </nav>
          </aside>

          <div className="space-y-6">
            {page.sections.map((section) => (
              <article
                key={section.title}
                id={section.title.toLowerCase().replace(/[^a-z0-9]+/g, '-')}
                className="card-minimal p-8 bg-white/80 space-y-5 scroll-mt-56 md:scroll-mt-32"
              >
                {section.eyebrow ? <span className="pill-minimal active">{section.eyebrow}</span> : null}
                <h2 className="text-4xl md:text-5xl font-serif tracking-tight">{section.title}</h2>
                <div className="space-y-4 text-lg text-ink-light leading-relaxed">
                  {section.body.map((paragraph) => (
                    <p key={paragraph}>{paragraph}</p>
                  ))}
                </div>
                {section.bullets?.length ? (
                  <div className="grid gap-3">
                    {section.bullets.map((item) => (
                      <div key={item} className="rounded-[22px] bg-sidebar/60 border border-border p-4 text-sm text-ink-light leading-relaxed">
                        {item}
                      </div>
                    ))}
                  </div>
                ) : null}
              </article>
            ))}
          </div>
        </section>

        <section className="card-minimal p-8 bg-[linear-gradient(135deg,rgba(41,67,42,0.08),rgba(255,255,255,0.92))] space-y-4">
          <span className="pill-minimal active">Keep planning</span>
          <h2 className="text-4xl font-serif tracking-tight">Choose the next Oregon truffle guide.</h2>
          <div className="flex flex-wrap gap-3">
            <Link to="/oregon-truffle-festival-travel-guide/" className="btn-minimal">Festival guide</Link>
            <Link to="/truffle-hunting-in-oregon/" className="btn-minimal">Hunting guide</Link>
            <Link to="/oregon-truffle-season/" className="btn-minimal">Season guide</Link>
            <Link to="/where-to-find-truffles-in-oregon/" className="btn-minimal">Where to find</Link>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
